import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Translate } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Elements where we must NOT interfere with native text interaction
const NO_TRANSLATE_SELECTOR =
  'input, textarea, button, select, [contenteditable=""], [contenteditable="true"], [data-no-translate], [data-testid^="editor-"]';

const isInsideInteractive = (el) => {
  if (!el) return true;
  if (!(el instanceof Element)) return false;
  return !!el.closest(NO_TRANSLATE_SELECTOR);
};

const isInsidePanel = (el) => !!(el instanceof Element && el.closest('[data-translator-panel]'));

const cleanWord = (raw) => {
  if (!raw) return '';
  // strip punctuation / quotes, keep letters + accents
  const t = String(raw).trim().replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '');
  return t.slice(0, 80);
};

/**
 * Global click-to-translate overlay.
 * - Double-click any word → popover with translation
 * - Select text → floating "Traducir" button
 * - Saves to flashcards via /api/flashcards
 * Skips inputs, textareas, buttons and elements with [data-no-translate].
 */
export default function GlobalTranslator() {
  // Landing / auth pages: no logged-in user. Skipping the click-to-translate
  // overlay there prevents the "please log in" prompt from firing every time
  // a visitor selects a headline to read or copy — they're not students yet,
  // this UX just gets in the way. Once authenticated, the widget is
  // available across the whole platform as before.
  const { user } = useAuth();
  const authed = !!user;

  const [pos, setPos] = useState(null); // { x, y, word, mode: 'popover'|'trigger' }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const panelRef = useRef(null);

  const close = useCallback(() => { setPos(null); setResult(null); setSaved(false); }, []);

  // Close on escape / outside click. We intentionally do NOT close on scroll:
  // minor layout shifts (entrance animation, iOS address-bar hide) fire scroll
  // events that used to dismiss the popover before the user could tap "Save".
  useEffect(() => {
    if (!pos) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onClick = (e) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) close();
    };
    window.addEventListener('keydown', onKey);
    // Register after current microtask so the opening click doesn't immediately close us.
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [pos, close]);

  const translate = useCallback(async (word) => {
    setLoading(true); setResult(null); setSaved(false);
    try {
      const res = await axios.post(
        `${API}/translate/word`, { word, direction: 'auto' },
        { withCredentials: true }
      );
      setResult(res.data);
    } catch {
      toast.error('Traducción no disponible');
      close();
    } finally { setLoading(false); }
  }, [close]);

  // ── Double-click handler ───────────────────────────────────────────────
  useEffect(() => {
    if (!authed) return undefined;
    const onDbl = (e) => {
      if (isInsideInteractive(e.target) || isInsidePanel(e.target)) return;
      const sel = window.getSelection();
      const word = cleanWord(sel ? sel.toString() : '');
      if (!word || word.length < 2) return;
      // Position panel near the cursor
      const x = Math.min(e.clientX, window.innerWidth - 300);
      const y = Math.min(e.clientY + 12, window.innerHeight - 180);
      setPos({ x, y, word, mode: 'popover' });
      translate(word);
    };
    document.addEventListener('dblclick', onDbl);
    return () => document.removeEventListener('dblclick', onDbl);
  }, [authed, translate]);

  // ── Long-press handler for mobile (touch devices have no dblclick) ────
  useEffect(() => {
    if (!authed) return undefined;
    let pressTimer = null;
    let startX = 0, startY = 0;
    let startTarget = null;

    const clear = () => {
      if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; }
    };
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return clear();
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY; startTarget = e.target;
      if (isInsideInteractive(startTarget) || isInsidePanel(startTarget)) return;
      clear();
      pressTimer = setTimeout(() => {
        // After 550 ms, if the finger hasn't moved, translate the word under it
        try {
          let word = '';
          // Use caretRangeFromPoint to find the exact word under the finger
          const range = document.caretRangeFromPoint?.(startX, startY);
          if (range) {
            const node = range.startContainer;
            if (node.nodeType === 3 /* text */) {
              const text = node.textContent || '';
              const idx = range.startOffset;
              // extend left
              let l = idx; while (l > 0 && /\S/.test(text[l - 1])) l--;
              let r = idx; while (r < text.length && /\S/.test(text[r])) r++;
              word = cleanWord(text.slice(l, r));
            }
          }
          if (!word) {
            // Fallback: if the user already highlighted something, use that
            const sel = window.getSelection();
            word = cleanWord(sel ? sel.toString() : '');
          }
          if (!word || word.length < 2) return;
          const x = Math.min(startX, window.innerWidth - 300);
          const y = Math.min(startY + 20, window.innerHeight - 200);
          setPos({ x, y, word, mode: 'popover' });
          translate(word);
          // Prevent the iOS context menu from stealing focus
          if (navigator.vibrate) navigator.vibrate(20);
        } catch {}
      }, 550);
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      if (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8) clear();
    };
    const onTouchEnd = () => clear();

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
    return () => {
      clear();
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [authed, translate]);

  // ── Selection → floating trigger button ────────────────────────────────
  useEffect(() => {
    if (!authed) return undefined;
    const onSelect = (e) => {
      // Don't hijack clicks that originate inside the translator panel: if the
      // user is interacting with our own popover (e.g. tapping "Guardar tarjeta"),
      // the mouseup lives inside it and must NOT downgrade the popover to a trigger.
      if (isInsidePanel(e.target) || isInsideInteractive(e.target)) return;
      // If a popover is already open, keep it — selection changes shouldn't
      // swap it for a trigger button (which unmounts the save button mid-click).
      if (pos && pos.mode === 'popover') return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const anchor = sel.anchorNode?.parentElement;
      if (isInsideInteractive(anchor) || isInsidePanel(anchor)) return;
      const text = cleanWord(sel.toString());
      if (!text || text.length < 2 || text.length > 80) return;
      // Ignore multi-word selections > 5 words (to avoid translating sentences)
      if (text.split(/\s+/).length > 5) return;
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect || (!rect.width && !rect.height)) return;
      const x = Math.min(rect.left + rect.width / 2 - 60, window.innerWidth - 140);
      const y = Math.max(rect.top - 40, 8);
      setPos({ x, y, word: text, mode: 'trigger' });
    };
    document.addEventListener('mouseup', onSelect);
    document.addEventListener('touchend', onSelect);
    return () => {
      document.removeEventListener('mouseup', onSelect);
      document.removeEventListener('touchend', onSelect);
    };
  }, [authed, pos]);

  const saveCard = async () => {
    if (!result || saved) return;
    try {
      await axios.post(`${API}/flashcards`, {
        word: pos.word,
        translation: result.translation,
        example_sentence: result.example_sentence || '',
        direction: result.direction || 'es->de',
      }, { withCredentials: true });
      setSaved(true);
      toast.success('Guardado en tus tarjetas', {
        action: {
          label: 'Ver tarjetas',
          onClick: () => { window.location.href = '/vocabulary'; },
        },
      });
      // Auto-close the popover shortly after to free the cursor
      setTimeout(() => close(), 1400);
    } catch (err) {
      const msg = err.response?.data?.detail || 'No se pudo guardar';
      toast.error(msg);
    }
  };

  if (!pos) return null;

  // Small "Traducir" button — the selection trigger
  if (pos.mode === 'trigger') {
    return (
      <div
        ref={panelRef}
        data-translator-panel
        style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999 }}
      >
        <button
          onClick={() => {
            setPos({ ...pos, mode: 'popover', y: Math.min(pos.y + 36, window.innerHeight - 200) });
            translate(pos.word);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#292524] text-white text-xs font-semibold shadow-xl hover:bg-[#AB1C24] transition-colors"
          data-testid="gt-translate-btn"
        >
          <Translate size={14} weight="bold" /> Traducir
        </button>
      </div>
    );
  }

  // Full popover
  return (
    <div
      ref={panelRef}
      data-translator-panel
      style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999, width: 280 }}
      className="bg-white rounded-xl border border-stone-200 shadow-2xl p-3.5 animate-in fade-in zoom-in-95 duration-150"
      data-testid="gt-popover"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#AB1C24]">
            {result?.direction || 'Traductor'}
          </p>
          <p className="font-bold text-[#292524] text-sm truncate" title={pos.word}>{pos.word}</p>
        </div>
        <button onClick={close} className="text-[#78716C] hover:text-[#292524]" data-testid="gt-close">
          <X size={16} />
        </button>
      </div>
      <div className="border-t border-stone-100 mt-2 pt-2 min-h-[40px]">
        {loading && <p className="text-xs text-[#78716C]">Traduciendo…</p>}
        {result && (
          <>
            <p className="text-sm text-[#292524] font-medium">→ {result.translation}</p>
            {result.example_sentence && (
              <p className="text-xs text-[#78716C] italic mt-1">"{result.example_sentence}"</p>
            )}
            <button
              onClick={saveCard}
              disabled={saved}
              className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded ${saved ? 'bg-emerald-100 text-emerald-700' : 'bg-[#AB1C24] text-white hover:bg-[#8A151C]'}`}
              data-testid="gt-save"
            >
              {saved ? '✓ Guardada' : <><Plus size={12} /> Guardar tarjeta</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
